import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnDestroy,
  ChangeDetectorRef
} from '@angular/core';
import { NgForm } from '@angular/forms';
import {
  IValueChangedEvent,
  IComponentStateHistory,
  BaseSetupTab,
  outputParams,
  REMOVED
} from '@nrwl/test-setup';
import { get, isNil } from 'lodash';

import { AGENT_MODEL, CommonRegEx, Debounce, TestMode, YangService } from '@nrwl/common-lib';

import { Subject, Subscription } from 'rxjs';
import { TruespeedSetupService } from '../../truespeed-setup.service';
import { TruespeedConfigFieldMapping as ConfigFieldMapping, DSCP_FLOWS } from '../models/model';
import { truespeedScema } from '../../config/Yang/truespeed';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'nrwl-truespeed-setup-advance-tab',
  templateUrl: './truespeed-setup-advance-tab.component.html',
  styleUrls: ['./truespeed-setup-advance-tab.component.scss']
})
export class TruespeedSetupAdvanceTabComponent implements OnInit, OnDestroy {
  @ViewChild('cidrInput') el: ElementRef;
  @Input() tab: BaseSetupTab;
  @Input() setupCtrl: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreValues?: Function;
  @Input() previousOutput: outputParams;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  @ViewChild('advanceForm', { static: true }) form: NgForm;
  readonly unsubscribe: Subject<void> = new Subject();
  validIPRegex: RegExp = CommonRegEx.IP_V4_V6;
  selectedOriginator: any;
  selectedDestination: any;
  isRunExclusiveDisabled = false;
  noOfWalkTimeoutId: any;
  formField = {
    tosDscp: null,
    connections: null,
    tcpPort: null,
    cirStart: null,
    downstream: null,
    upstream: null,
    noOfwalk: null,
    duration: null,
    saturation: null,
    dynamicSaturation: null,
    boostWindow: null,
    boostConnection: null,
    runExclusive: null,
    expirationTime: null,
    maxExecutionCounts: null,
    startingPercent: null,
    ipAddress: null
  };
  formTooltip = {
    tcpPort:
      'When First Available is chosen the test device will select the first unused port from its list of available ports when test execution begins. If all ports are used the test will remain queued on the controller until other tests complete.',
    runExclusiveOnAgent:
      'Enable this option will make this test run exclusively on the measurement agent.',
    numberOfWalks:
      'Specify the number of window walks to run in each direction. If the "Run Saturation Window" option is selected, this value can be 0. If the "Starting % of CIR" option is 100, this value can only be 1. Otherwise it must be in the range from 2 to 10.',
    numberOfConnections:
      'By default, TrueSpeed uses a proprietary algorithm to select a TCP connection count that is appropriate for the network CIR and Round Trip Time (RTT). It is based on RFC-6349, but can be manually configured. When testing over Long Fat Networks (LFNs), selecting a single (1) connection may induce network congestion and loss due to the size of the Bandwidth Delay Product (BDP) over an LFN.',
    addSaturationWindow:
      'Selecting this runs one additional TCP test in each direction that is configured to run a TCP window greater than the one based on the CIR to determine the maximum network bandwidth. The boost settings can be used to deflate or inflate the window size and/or connection count used by the saturation window test from 0 to 100%.'
  };
  boostInputEnable = true;
  numberOfWalkUpdated = false;
  dscpFlows = DSCP_FLOWS;
  tcpPortData: Array<{ name: string; value: number }>;
  defaultDynamicSaturation = false;
  showDynamicSaturation = false;
  showRunExclusive = false;
  // Error variable declararion

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isTCPPortError: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ispercentStartWindowWalkError: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isNoOfWalkError: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isConnectionError: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isUpstreamError: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isDownStreamError: any;
  isAgentToJmep = false;
  minCir = 1;
  tabsubscription: Subscription;
  maxConnections = 64;
  private streamSubscription: Subscription;
  constructor(
    public setupService: TruespeedSetupService,
    readonly yangService: YangService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.tab.form = this.form;
    this.initializeData();
    if (this.setupService.tabDataChangeEvents) {
      this.tabsubscription = this.setupService.tabDataChangeEvents.subscribe(event => {
        if (event === 1) {
          this.restoreFormValues();
        }
      });
    }
    this.streamSubscription = this.setupService.streamDataChangeEvents.subscribe(data => {
      if (data) {
        if (data.key === ConfigFieldMapping.UPSTREAM_CIR) {
          this.formField.upstream = data.value;
        }
        if (data.key === ConfigFieldMapping.DOWNSTREAM_CIR) {
          this.formField.downstream = data.value;
        }
        this.onDataEmit(data.key, data.value, data.valid);
      }
    });
  }

  initializeData(): void {
    this.restoreFormValues();
    if (this.setupService.mode === 'new' && this.setupService.controllerConfig) {
      this.setupService.controllerConfig.pipe(takeUntil(this.unsubscribe)).subscribe(response => {
        if (response.status === 'success') {
          let saturationEnabled = false;
          if (typeof response.data.test.testRunSaturation !== 'undefined') {
            const saturationOption = response.data.test.testRunSaturation.toString();
            saturationEnabled =
              saturationOption.toLowerCase() == 'yes' || saturationOption.toLowerCase() == 'true';
            this.onDataEmit(ConfigFieldMapping.SATURATION, saturationEnabled, true);
          }

          if (typeof response.data.test.testDynamicSaturation !== 'undefined') {
            const dynamicSatOption = response.data.test.testDynamicSaturation.toString();
            this.defaultDynamicSaturation =
              dynamicSatOption.toLowerCase() == 'yes' ||
              (dynamicSatOption.toLowerCase() == 'true' && saturationEnabled);
            this.showDynamicSaturation = !isNil(
              this.restoreFieldValues(ConfigFieldMapping.ENABLE_DYNAMIC_SATURATION)
            );
            if (this.showDynamicSaturation) {
              this.onDataEmit(
                ConfigFieldMapping.ENABLE_DYNAMIC_SATURATION,
                this.defaultDynamicSaturation,
                true
              );
            }
          }

          if (typeof response.data.test.testNumberOfWalks !== 'undefined') {
            this.onDataEmit(
              ConfigFieldMapping.NUMBER_OF_WALKS,
              response.data.test.testNumberOfWalks,
              true
            );
          }
          if (typeof response.data.test.testStartingPercentOfCir !== 'undefined') {
            this.onDataEmit(
              ConfigFieldMapping.STARTING_PERCENT,
              response.data.test.testStartingPercentOfCir,
              true
            );
          }
        }
        this.restoreFormValues();
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  onDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.complete();
    this.unsubscribe.next();
    if (this.tabsubscription) {
      this.tabsubscription.unsubscribe();
    }
    if (this.streamSubscription) {
      this.streamSubscription.unsubscribe();
    }
    if (this.noOfWalkTimeoutId) {
      clearTimeout(this.noOfWalkTimeoutId);
    }
  }
  restoreFormValues(): void {
    this.isAgentToJmep = this.setupService.output.testMode === TestMode.MA_TO_JMEP ? true : false;
    this.selectedOriginator = this.restoreFieldValues(ConfigFieldMapping.SELECTED_ORIGINATOR);
    this.selectedDestination = this.restoreFieldValues(ConfigFieldMapping.SELECTED_DESTINATION);
    this.formField.upstream = this.restoreFieldValues(ConfigFieldMapping.UPSTREAM_CIR);
    this.formField.downstream = this.restoreFieldValues(ConfigFieldMapping.DOWNSTREAM_CIR);
    this.formField.noOfwalk = this.restoreFieldValues(ConfigFieldMapping.NUMBER_OF_WALKS);
    this.formField.duration = this.restoreFieldValues(ConfigFieldMapping.WINDOW_DURATION);
    this.formField.tosDscp = this.restoreFieldValues(ConfigFieldMapping.DSCP);
    this.formField.ipAddress = this.restoreFieldValues(ConfigFieldMapping.DESTINATION_IP);
    this.formField.boostWindow = this.restoreFieldValues(ConfigFieldMapping.BOOST_WINDOW_PERCENT);
    this.formField.boostConnection = this.restoreFieldValues(
      ConfigFieldMapping.BOOST_CONNECTION_PERCENT
    );
    this.formField.runExclusive = this.restoreFieldValues(ConfigFieldMapping.RUN_EXCLUSIVE);
    this.showRunExclusive = !isNil(this.restoreFieldValues(ConfigFieldMapping.RUN_EXCLUSIVE));
    this.formField.tcpPort = this.restoreFieldValues(ConfigFieldMapping.TCP_PORT);
    const connectionValue = this.restoreFieldValues(ConfigFieldMapping.CONNECTIONS);
    this.formField.connections = connectionValue && connectionValue > 0 ? connectionValue : null;
    this.formField.saturation = this.restoreFieldValues(ConfigFieldMapping.SATURATION);
    this.formField.dynamicSaturation = this.restoreFieldValues(
      ConfigFieldMapping.ENABLE_DYNAMIC_SATURATION
    );
    this.showDynamicSaturation = !isNil(
      this.restoreFieldValues(ConfigFieldMapping.ENABLE_DYNAMIC_SATURATION)
    );
    this.boostInputEnable = this.showDynamicSaturation
      ? !this.formField.dynamicSaturation && this.formField.saturation
      : this.formField.saturation;
    this.restoreCIRValue();
    this.tcpPortData = this.setupService.tcpPorts;
    if (!this.tcpPortData.length) {
      this.tcpPortData = this.setupService.defaultTcpPort;
    }
    this.isRunExclusiveDisabled = this.selectedOriginator?.getModel() === AGENT_MODEL.JMEP_10G;
    this.maxConnections =
      this.selectedOriginator?.getModel() === AGENT_MODEL.JMEP_10G ||
      this.selectedDestination?.getModel() === AGENT_MODEL.JMEP_10G
        ? 60
        : 64;
    this.noOfConnectionsUpdated();
  }

  //eslint-disable-next-line
  restoreFieldValues(field: string): any {
    const data = this.stateHistory(this.componentId, field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  restoreCIRValue(): void {
    const cir = this.stateHistory(this.componentId, ConfigFieldMapping.STARTING_PERCENT);
    if (cir && cir.value && cir.value !== REMOVED) {
      this.formField.cirStart = cir.value;
      if (this.formField.cirStart < 100 && this.formField.cirStart >= 1) {
        this.numberOfWalkUpdated = true;
      } else {
        this.numberOfWalkUpdated = false;
      }
    }
  }

  @Debounce(500)
  downstreamUpdated(): void {
    this.formField.downstream = this.setupService.roundToOneDecimal(this.formField.downstream);
    const valid = this.validityStreamCheck(
      this.formField.downstream,
      this.setupService.maxDownStream,
      1
    );
    this.onDataEmit(ConfigFieldMapping.DOWNSTREAM_CIR, this.formField.downstream, valid);
    this.setupService.streamDataChanged({
      key: ConfigFieldMapping.DOWNSTREAM_CIR,
      value: this.formField.downstream,
      valid: valid
    });
  }

  @Debounce(500)
  upstreamUpdated(): void {
    const valid = this.validityStreamCheck(
      this.formField.upstream,
      this.setupService.maxUpStream,
      1
    );
    this.formField.upstream = this.setupService.roundToOneDecimal(this.formField.upstream);
    this.onDataEmit(ConfigFieldMapping.UPSTREAM_CIR, this.formField.upstream, valid);
    this.setupService.streamDataChanged({
      key: ConfigFieldMapping.UPSTREAM_CIR,
      value: this.formField.upstream,
      valid: valid
    });
  }

  isValidConnectionsRange(): boolean {
    const connectionValue = this.formField.connections ?? 0;
    let isValid = true;
    if (connectionValue > 0) {
      if (connectionValue > this.maxConnections) {
        isValid = false;
      }
    }
    return isValid;
  }

  noOfConnectionsUpdated(): void {
    const connectionValue = this.formField.connections ?? 0;
    this.onDataEmit(
      ConfigFieldMapping.CONNECTIONS,
      connectionValue,
      this.isValidConnectionsRange()
    );
  }

  // eslint-disable-next-line
  onTcpPortChange($event: any): void {
    this.formField.tcpPort = $event.value;
    this.onDataEmit(ConfigFieldMapping.TCP_PORT, this.formField.tcpPort, true);
  }

  // eslint-disable-next-line
  onDscpValueChange($event: any): void {
    this.formField.tosDscp.dscp = $event.value;
    this.onDataEmit(ConfigFieldMapping.DSCP, this.formField.tosDscp, true);
  }
  // eslint-disable-next-line
  onIPValueChange(value: any): void {
    this.formField.ipAddress = value;
    this.onDataEmit(ConfigFieldMapping.DESTINATION_IP, this.formField.ipAddress, true);
  }

  cirStartUpdated(): void {
    const percentStartWindowWalk = this.formField.cirStart;
    const yangModel = this.setupService.formatYangBaseModel(
      ConfigFieldMapping.STARTING_PERCENT,
      percentStartWindowWalk
    );
    this.ispercentStartWindowWalkError = this.yangService.validateYangModel(
      truespeedScema,
      yangModel
    );

    if (this.formField.cirStart < 100 && this.formField.cirStart >= 1) {
      this.numberOfWalkUpdated = true;
    } else {
      this.numberOfWalkUpdated = false;
    }
    this.onDataEmit(
      ConfigFieldMapping.STARTING_PERCENT,
      this.formField.cirStart,
      this.isValidYangModel(this.ispercentStartWindowWalkError)
    );
    this.validateNoOfWalk();
  }

  validityStreamCheck(data: number, max: number, min: number): boolean {
    if (this.isAgentToJmep) {
      this.minCir = 20;
    } else {
      this.minCir = 1;
    }
    return data || data === 0 ? +data <= max && +data >= min : false;
  }

  validateNoOfWalk(): void {
    if (this.formField.noOfwalk == null) return;
    let minVal = this.formField.saturation ? 0 : 1;
    const maxVal = this.formField.cirStart === 100 ? 1 : 10;
    if (this.formField.cirStart !== 100) {
      minVal = 2;
    }
    const isValid = this.validityStreamCheck(this.formField.noOfwalk, maxVal, minVal);

    // cirStart = 100 → noOfwalk must be 1
    if (!isValid && this.formField.cirStart === 100) {
      if (this.noOfWalkTimeoutId) {
        clearTimeout(this.noOfWalkTimeoutId);
      }
      this.noOfWalkTimeoutId = setTimeout(() => {
        this.form.controls.noOfwalk.setErrors(null);
        this.formField.noOfwalk = 1;
        this.emitNoOfWalk(1, maxVal, minVal);
      }, 0);
      return;
    }

    this.form.controls.noOfwalk.setErrors(
      isValid ? null : { invalidRange: `valid range is ${minVal}...${maxVal}` }
    );
    this.emitNoOfWalk(this.formField.noOfwalk, maxVal, minVal);
  }

  private emitNoOfWalk(value: number, maxVal: number, minVal: number): void {
    this.onDataEmit(
      ConfigFieldMapping.NUMBER_OF_WALKS,
      value,
      this.validityStreamCheck(value, maxVal, minVal)
    );
  }

  private getDurationLimits() {
    let minVal = 15;
    let maxVal = 360;
    if (
      this.selectedOriginator?.getModel() === AGENT_MODEL.JMEP_10G ||
      this.selectedDestination?.getModel() === AGENT_MODEL.JMEP_10G
    ) {
      maxVal = 100;
    }
    if (
      this.setupService.output.testMode === TestMode.CLIENT_TO_MA &&
      this.formField.saturation &&
      this.formField.dynamicSaturation
    ) {
      minVal = 30;
    }
    return { minVal, maxVal };
  }

  boostWindowUpdated(): void {
    this.onDataEmit(
      ConfigFieldMapping.BOOST_WINDOW_PERCENT,
      this.formField.boostWindow,
      this.validityStreamCheck(this.formField.boostWindow, 100, 0)
    );
  }

  boostConnectionUpdated(): void {
    this.onDataEmit(
      ConfigFieldMapping.BOOST_CONNECTION_PERCENT,
      this.formField.boostConnection,
      this.validityStreamCheck(this.formField.boostConnection, 100, 0)
    );
  }

  durationUpdated(): void {
    const { minVal, maxVal } = this.getDurationLimits();
    const isValid = this.validityStreamCheck(this.formField.duration, maxVal, minVal);
    this.form.controls.duration.setErrors(
      isValid ? null : { invalidRange: `valid range is ${minVal}...${maxVal}` }
    );
    this.onDataEmit(ConfigFieldMapping.WINDOW_DURATION, this.formField.duration, isValid);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  saturationChanged($event: any): void {
    this.formField.saturation = $event.checked;
    if (this.showDynamicSaturation) {
      this.boostInputEnable = !this.formField.dynamicSaturation && this.formField.saturation;
    } else {
      this.boostInputEnable = this.formField.saturation;
    }
    this.validateNoOfWalk();
    this.onDataEmit(ConfigFieldMapping.SATURATION, this.formField.saturation, true);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  enableDynamicSaturation($event: any): void {
    if ($event.checked) {
      this.boostInputEnable = false;
      this.formField.dynamicSaturation = true;
      this.formField.boostConnection = 40;
      this.formField.boostWindow = 20;
    } else {
      this.boostInputEnable = true;
      this.formField.dynamicSaturation = false;
    }
    this.boostConnectionUpdated();
    this.boostWindowUpdated();
    this.onDataEmit(
      ConfigFieldMapping.ENABLE_DYNAMIC_SATURATION,
      this.formField.dynamicSaturation,
      true
    );
    this.durationUpdated();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  runOnAgentChange($event: any): void {
    this.formField.runExclusive = $event.checked;
    this.onDataEmit(ConfigFieldMapping.RUN_EXCLUSIVE, this.formField.runExclusive, true);
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  isValidYangModel(resultSet: any): boolean {
    if (typeof resultSet === 'string') {
      return false;
    } else {
      return true;
    }
  }
}
